import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { faMailbox } from '@fortawesome/pro-duotone-svg-icons';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select, Store } from '@ngxs/store';
import _, { toNumber } from 'lodash';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { shortcutStringBuilder } from 'src/app/helper/shortcutStringBuilder';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { Key } from 'src/app/samwebDescription/samweb/shortcuts/Keys';
import { ShortcutDescription } from 'src/app/samwebDescription/samweb/shortcuts/shortcutDescription';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { EndEmailProcess } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.actions';
import { MessageInfo } from 'src/app/store/samwin/samwin-mail-list/message-info';
import { MoveMessage, RemoveFromMessages, UpdateMessageDroppableStatus } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.actions';
import { SamwinMessageListState } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.state';
import {
   MessageTreeviewEntrySelected,
   MessageTreeviewItemCollapse,
   MessageTreeviewItemCollapseSelectedEntry,
   MessageTreeviewItemExpand,
   MessageTreeviewItemExpandSelectedEntry,
   MessageTreeviewSelectNextEntry,
} from 'src/app/store/samwin/samwin-mail-skill-browser/samwin-mail-skill-browser.actions';
import { SamwinMessageSkillBrowserState } from 'src/app/store/samwin/samwin-mail-skill-browser/samwin-mail-skill-browser.state';
import { MailingFolderEnum } from 'src/app/store/samwin/samwin-skills/mailing-folders-enum';
import { MailingSkillTreeItem } from 'src/app/store/samwin/samwin-skills/mailing-skill-tree-item';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SkillConstants } from 'src/app/store/samwin/samwin-skills/skill-constants';
import { SessionState } from 'src/app/store/session/session.state';
import { BasicBrickComponent } from '../../../basic-brick-component';
import { SamwebBrick } from '../../../samweb-brick.decorator';

@Component({
   selector: 'samweb-skill-browser-mail',
   templateUrl: './skill-browser-mail.component.html',
   styleUrls: ['./skill-browser-mail.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'skillBrowserMail',
   brickTitleTranslationKey: 'brick.brick_definitions.skill-browser-mail.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.skill-browser-mail.description',
   brickIcon: faMailbox,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.MAILING,
   requiredFeature: FeatureEnum.SAMWIN_7_EMAIL,
})
export class SkillBrowserMailComponent extends BasicBrickComponent implements OnInit {
   public MailingSkillTree: MailingSkillTreeItem[];

   @SelectSnapshot(SessionState.getAuthenticatedUserId)
   public userId: number;

   public selectedKeys$: Observable<string[]>;

   @Select(SamwinMessageListState.canMessageBeDropped)
   public isMessageDroppable$: Observable<boolean>;

   @Select(SamwinMessageListState.isMessageDragged)
   public isMessageDragged$: Observable<boolean>;

   @Select(SamwinMessageSkillBrowserState.getExpandedItems)
   public keys$: Observable<string[]>;

   public SKILL_TREEVIEW_NAME = SkillConstants.SKILL_TREEVIEW_NAME;

   constructor(public store: Store) {
      super();
   }

   public keyActions: ShortcutDescription[] = [
      {
         shortcutKey: shortcutStringBuilder(Key.ArrowUp),
         action: () => {
            console.debug('[HotKey] Skill Browser Mail Pressed ArrowUp');
            this.store.dispatch(new MessageTreeviewSelectNextEntry('up'));
            return true;
         },
      },
      {
         shortcutKey: shortcutStringBuilder(Key.ArrowDown),
         action: () => {
            console.debug('[HotKey] Skill Browser Pressed ArrowDown');
            this.store.dispatch(new MessageTreeviewSelectNextEntry('down'));
            return true;
         },
      },
      {
         shortcutKey: shortcutStringBuilder(Key.ArrowLeft),
         action: () => {
            console.debug('[HotKey] Skill Browser Mail Pressed ArrowLeft');
            this.store.dispatch(new MessageTreeviewItemCollapseSelectedEntry());
            return true;
         },
      },
      {
         shortcutKey: shortcutStringBuilder(Key.ArrowRight),
         action: () => {
            console.debug('[HotKey] Skill Browser Pressed ArrowRight');
            this.store.dispatch(new MessageTreeviewItemExpandSelectedEntry());
            return true;
         },
      },
   ];

   public getChildren = (dataitem: MailingSkillTreeItem): Observable<MailingSkillTreeItem[]> =>
      this.store.select(SamwinSkillsState.getMailingSkillTreeChildren).pipe(
         distinctUntilChanged((prev, curr) => _.isEqual(prev(dataitem.groupKey), curr(dataitem.groupKey))),
         map((getChildren) => getChildren(dataitem.groupKey))
      );

   public hasChildren = (dataitem: MailingSkillTreeItem): boolean => _.isNil(dataitem.skillId);

   ngOnInit(): void {
      this.selectedKeys$ = this.store.select(SamwinMessageSkillBrowserState.selectedSkillId).pipe(map((selectedSkill) => [selectedSkill as string]));
      this.MailingSkillTree = this.store.selectSnapshot(SamwinSkillsState.getMailingSkillTree);
   }

   public handleCollapse(node: { index: string; dataItem: MailingSkillTreeItem }) {
      return this.store.dispatch(new MessageTreeviewItemCollapse(node.dataItem.groupKey));
   }

   public handleExpand(node: { index: string; dataItem: MailingSkillTreeItem }) {
      return this.store.dispatch(new MessageTreeviewItemExpand(node.dataItem.groupKey));
   }

   public onSelectionChange(node: { index: string; dataItem: MailingSkillTreeItem }) {
      return this.store.dispatch(new MessageTreeviewEntrySelected(node.dataItem.groupKey));
   }

   onDropEntered(event: CdkDragDrop<string[]>) {
      const targetItems: string[] = event.container.id.split('_');
      const targetSkill: number = toNumber(_(targetItems).last());
      const targetGroup = _(targetItems).head();
      const mailingFolder: MailingFolderEnum = MailingFolderEnum[targetGroup];

      const itemData: MessageInfo = event.item.data;
      const currentSkill = itemData.skill.skillId;
      const currentFolder = this.store.selectSnapshot(SamwinMessageListState.getGroupFilter);

      const isCurrentFolder = mailingFolder.name === currentFolder;
      const isOutboxFolder = mailingFolder.name === MailingFolderEnum.SENT.name || mailingFolder.name === MailingFolderEnum.OUTBOX.name;
      const isSameSkill = !_.isNaN(targetSkill) && currentSkill === targetSkill;

      let mayDrop = true;
      if (!_.isNaN(targetSkill)) {
         const isAccessible = this.store.selectSnapshot(SamwinSkillsState.getIsAccessibleMailingSkill)(targetSkill);
         const isSkillGroup = this.store.selectSnapshot(SamwinSkillsState.getSkillGroupById)(targetSkill).skills.length > 1;
         mayDrop = isAccessible && !isSkillGroup;
      }

      const dontDrop = (isCurrentFolder && _.isNaN(targetSkill)) || (isSameSkill && isCurrentFolder) || isOutboxFolder;
      this.store.dispatch(new UpdateMessageDroppableStatus(!dontDrop && mayDrop));
   }

   onDropExit() {
      this.store.dispatch(new UpdateMessageDroppableStatus(false));
   }

   onDrop(event: CdkDragDrop<string[]>) {
      if (this.store.selectSnapshot(SamwinMessageListState.canMessageBeDropped)) {
         const itemData: MessageInfo = event.item.data;
         const targetItems: string[] = event.container.id.split('_');
         const targetSkill: number = toNumber(_(targetItems).last());
         const targetGroup = _(targetItems).head();
         const mailingFolder: MailingFolderEnum = MailingFolderEnum[targetGroup];

         this.store.dispatch(new MoveMessage(itemData.messageId, mailingFolder.value, _.isNaN(targetSkill) ? itemData?.skill?.skillId : targetSkill));
         this.store.dispatch(new RemoveFromMessages(itemData.messageId));
         // also close inbox mails, because waitlist item is not available after drop.
         this.store.dispatch(new EndEmailProcess(itemData.messageId));
      }
   }
}
